import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  alpha,
  styled,
  useTheme,
} from '@mui/material';
import { Logout as LogoutIcon, ChevronRight, Person } from '@mui/icons-material';
import { useAuth } from 'src/features/authentication';
import { useNavigate } from 'react-router-dom';
import pages from 'src/router/routes';

const UserSectionWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderTop: `1px solid ${theme.colors.alpha.black[10]}`,
  background: alpha(theme.colors.alpha.white[100], 0.8),
  backdropFilter: 'blur(10px)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const SidebarUserSection: React.FC = () => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(pages.auth.login.path);
    } catch (error) {
      console.error('Logout failed:', error);
    }
    handleClose();
  };

  const handleProfile = () => {
    navigate(pages.profile.path);
    handleClose();
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <UserSectionWrapper>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={user?.photoURL ?? ''}
            sx={{ width: 32, height: 32, border: `2px solid ${theme.colors.primary.main}` }}
          >
            {user?.displayName?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ textAlign: 'left', minWidth: 0 }}>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 600,
                color: theme.sidebar.textColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '140px',
              }}
            >
              {user?.displayName || 'User'}
            </Typography>
            <Typography
              sx={{
                color: alpha(theme.sidebar.textColor || theme.palette.text.secondary, 0.7),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '140px',
                display: 'block',
              }}
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleClick}
          color='primary'
          sx={{ minWidth: 'auto', padding: 1, color: theme.sidebar.textColor }}
        >
          <ChevronRight />
        </Button>
      </UserSectionWrapper>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: {
              padding: 0,
              minWidth: 150,
            },
          },
        }}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <Person fontSize='small' />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1, mx: 1 }} />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarUserSection;
